import { articleRepeatIndex } from "components/ContractsSettings/ContractEditorLexical/RepeatablePlugin/repeatBy";
import { agreementVariablesList } from "components/ContractsSettings/ContractEditorLexical/variables";
import type { UserVariables } from "components/shared/AgreementModal";
import { isStructuralAgreementVariableKey } from "containers/ContractValidationEdit/ContractValidationRightPanel/PanelVariables/agreementVariableKeys";
import { getAgreementWorkTitles } from "containers/ContractValidationEdit/ContractValidationRightPanel/PanelVariables/agreementTitleSource";
import { useMemo } from "react";
import type { Agreement, AgreementValidator } from "store/agreementsStore";
import type { IUser } from "types";

/**
 * This function format data for empty fields
 * @param isDraft Check if agreement status is "draft"
 * @param userVariables Variables state with value
 * @param agreement Agreement data
 * @returns Empty fields data for global fields or beneficiaries fields
 */
export const useMissingVariables = (
    isDraft: boolean,
    userVariables: UserVariables | undefined,
    agreement: Agreement | null,
) => {
    const { variablesLabels } = agreementVariablesList;

    return useMemo(() => {
        /* one of unused value send from backend at contract creation for userVariables
         ** waiting userVariable to get formatted before (userVariables?.vatBlock)
         */
        if (!isDraft || userVariables?.vatBlock)
            return {
                missingVariables: [],
                missingVariablesByBeneficiary: {},
                beneficiariesMissingVariables: [],
            } as MissingVariablesData;

        // Format global empty variables fields
        const missingVariables: MissingVariable[] = [];
        for (const variable in userVariables) {
            if (
                isStructuralAgreementVariableKey(variable) ||
                variable.includes("beneficiaries.") ||
                variable.startsWith("articles.")
            )
                continue;
            if (userVariables[variable] !== "") continue;
            const variableName = variablesLabels[`{{${variable}}}`] ?? `{{${variable}}}`;
            missingVariables.push({ name: variableName, key: variable });
        }

        // Format beneficiaries empty variables fields
        const beneficiaries = agreement?.userVariables?.beneficiaries;
        const missingVariablesByBeneficiary: Record<string, MissingVariable[]> = {};
        const beneficiariesMissingVariables = [] as MissingVariable[];

        beneficiaries?.forEach((beneficiary, index) => {
            const beneficiaryVariables: MissingVariable[] = []; // missing variables for a beneficiary
            for (const variable in userVariables) {
                if (variable.includes(`beneficiaries.${index}`)) {
                    const variableEl = document.querySelector(`[multi-beneficiary-variable-key="${variable}"]`);
                    const variableName = variableEl?.getAttribute("data-variable-name") as string;
                    const variableLabel = variablesLabels[variableName] ?? variableName;
                    const missingVar = { name: variableLabel, key: variable };
                    if (userVariables[variable] === "") {
                        beneficiaryVariables.push(missingVar);
                        beneficiariesMissingVariables.push({ ...missingVar, beneficiaryName: beneficiary.name ?? "" });
                    }
                }
            }
            missingVariablesByBeneficiary[beneficiary.name ?? ""] = beneficiaryVariables;
        });

        const workTitles = [...getAgreementWorkTitles(userVariables)].sort((left, right) => left.id - right.id);
        const articleIndexes = new Set<number>();
        for (const variable in userVariables) {
            const index = articleRepeatIndex(variable);
            if (index != null) articleIndexes.add(index);
        }
        articleIndexes.forEach((index) => {
            const articleName = workTitles[index]?.name || workTitles[index]?.ean13 || `Article ${index + 1}`;
            const articleVariables: MissingVariable[] = [];
            for (const variable in userVariables) {
                if (!variable.startsWith(`articles.${index}.`) || userVariables[variable] !== "") continue;
                const variableEl = document.querySelector(`[multi-beneficiary-variable-key="${variable}"]`);
                const variableName = variableEl?.getAttribute("data-variable-name") ?? "";
                const variableLabel = variablesLabels[variableName] ?? variableName;
                const missingVar = { name: variableLabel, key: variable };
                articleVariables.push(missingVar);
                beneficiariesMissingVariables.push({ ...missingVar, beneficiaryName: articleName });
            }
            const existing = missingVariablesByBeneficiary[articleName] ?? [];
            missingVariablesByBeneficiary[articleName] = [...existing, ...articleVariables];
        });

        return {
            // Global missing variables array
            missingVariables,
            // Missing variables object by beneficiary
            missingVariablesByBeneficiary,
            // Missing variables for beneficiaries inside array
            beneficiariesMissingVariables,
        } as MissingVariablesData;
    }, [userVariables]);
};

export type MissingVariablesData = {
    missingVariables: MissingVariable[];
    missingVariablesByBeneficiary: Record<string, MissingVariable[]>;
    beneficiariesMissingVariables: MissingVariable[];
};

export type MissingVariable = { name: string; key: string; beneficiaryName?: string };

export type AgreementValidatorExtended = IUser & {
    status?: string;
    fullName: string;
    created: string;
    modified: string;
};

/**
 * Format data for agreement validators
 * @param usersMap Users map (id -> user) for O(1) lookup
 * @param agreementValidators Agreement validators raw data
 */
export const agreementValidatorsDataTranform = (
    usersMap: Map<number, IUser> | undefined,
    agreementValidators: AgreementValidator[],
) => {
    if (!usersMap) return [];
    return [...agreementValidators]
        .sort((a, b) => a?.order - b?.order)
        .map((validator) => {
            const { status, createdAt: created, modifiedAt: modified } = validator;
            const user = usersMap.get(validator.userId)!;
            const fullName = `${user?.firstname ?? ""} ${user?.lastname ?? ""}`;

            return { ...user, status, fullName, created, modified };
        }) as AgreementValidatorExtended[];
};
